import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import { Link } from 'react-router-dom';

export default function LearningPaths() {
  const learningPaths = [
    {
      id: 1,
      title: '传统财税到智能财税',
      description: '从基础理论到智能应用的完整转型路径',
      duration: '12-16周',
      difficulty: '中级',
      color: 'bg-blue-500',
      steps: [
        '会计基础',
        '税务会计',
        '纳税申报实训',
        '智慧化税费申报与管理',
        '智能财税'
      ],
      image: 'https://public.readdy.ai/ai/img_res/714ec31297f4eac36d53d298d9973cf1.jpg'
    },
    {
      id: 2,
      title: '财务管理到风险控制',
      description: '系统掌握财务管理与风险控制核心技能',
      duration: '10-12周',
      difficulty: '中高级',
      color: 'bg-purple-500',
      steps: [
        '会计基础',
        '企业财务管理',
        '企业内部控制与风险管理',
        '财务审计'
      ],
      image: 'https://readdy.ai/api/search-image?query=Financial%20risk%20management%20pathway%20with%20charts%2C%20graphs%2C%20risk%20assessment%20tools%2C%20purple%20color%20scheme%2C%20professional%20business%20environment%2C%20strategic%20planning%20visualization&width=400&height=250&seq=path2&orientation=landscape'
    },
    {
      id: 3,
      title: '技术技能发展路径',
      description: '从基础工具到高级数据分析与自动化',
      duration: '14-18周',
      difficulty: '中高级',
      color: 'bg-teal-500',
      steps: [
        'Excel在财务中的应用',
        '大数据技术在财务中的应用',
        '财税大数据分析',
        '财税机器人开发与维护'
      ],
      image: 'https://readdy.ai/api/search-image?query=Technology%20skills%20development%20pathway%20showing%20progression%20from%20Excel%20to%20big%20data%20analytics%20and%20automation%2C%20teal%20color%20scheme%2C%20modern%20tech%20workspace%2C%20data%20visualization&width=400&height=250&seq=path3&orientation=landscape'
    },
    {
      id: 4,
      title: '征管实务发展路径',
      description: '专注税收征管的数字化与智能化应用',
      duration: '8-10周',
      difficulty: '中级',
      color: 'bg-green-500',
      steps: [
        '税收征管流程实训',
        '税务管理',
        '业财管理信息系统应用'
      ],
      image: 'https://readdy.ai/api/search-image?query=Tax%20administration%20pathway%20with%20digital%20systems%2C%20government%20compliance%20tools%2C%20green%20color%20scheme%2C%20professional%20tax%20office%20environment%2C%20digital%20workflow%20management&width=400&height=250&seq=path4&orientation=landscape'
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case '初级': return 'bg-green-100 text-green-800';
      case '中级': return 'bg-yellow-100 text-yellow-800';
      case '中高级': return 'bg-orange-100 text-orange-800';
      case '高级': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            优化学习路径
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            根据不同专业方向和兴趣，为您推荐最适合的学习路径，确保学习效果最大化
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {learningPaths.map((path) => (
            <Card key={path.id} hover className="h-full">
              <div className="relative">
                <img 
                  src={path.image} 
                  alt={path.title}
                  className="w-full h-48 object-cover object-top rounded-lg mb-4"
                />
                <div className={`absolute top-4 left-4 ${path.color} text-white px-3 py-1 rounded-full text-sm font-medium`}>
                  路径 {path.id}
                </div>
              </div>

              <div className="flex justify-between items-start mb-3">
                <h3 className="text-xl font-bold text-gray-900">{path.title}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(path.difficulty)}`}>
                  {path.difficulty}
                </span>
              </div>

              <p className="text-gray-600 mb-4 text-sm">{path.description}</p>

              <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <i className="ri-time-line mr-1"></i>
                  {path.duration}
                </div>
                <div className="flex items-center">
                  <i className="ri-book-line mr-1"></i>
                  {path.steps.length} 个阶段
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-3 text-sm">学习阶段：</h4>
                <div className="space-y-2">
                  {path.steps.map((step, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <div className={`w-6 h-6 ${path.color} text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0`}>
                        {index + 1}
                      </div>
                      <span className="text-gray-700">{step}</span>
                      {index < path.steps.length - 1 && (
                        <i className="ri-arrow-right-line text-gray-400 ml-2"></i>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Button variant="outline" className="w-full">
                <Link to="/learning-paths" className="flex items-center justify-center w-full">
                  <i className="ri-roadmap-line mr-2"></i>
                  选择此路径
                </Link>
              </Button>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/learning-paths">
            <Button size="lg">
              <i className="ri-compass-3-line mr-2"></i>
              获取个性化学习建议
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
